<template>
  <div :class="$style.shell">
    <aside :class="$style.tabs">
      <NavigationTabMenu
        :internal-user="isInternalUser"
        :new-notifications="newNotifications"
        @notification="notification"
      />
    </aside>

    <Main :class="$style.main">
      <Header
        :new-notifications="newNotifications"
        @notification="notification"
      />
      <Space200 />
      <div
        v-if="isInternalUser && store.isFloristUnauthorized && localStorageNotEmpty"
        :class="$style.banner"
      >
        <Space200 />
        <Banner
          type="error"
          :text="$t('suspendedFlorist')"
        />
        <Space200 />
      </div>

      <div>
        <NotificationListStacked
          v-if="!displayPanel"
          :is-toaster="true"
          :need-close="true"
          :notifications="filteredNotifications"
          @processed-cancel-order="onProcessedCancelOrder"
          @dismissed-notification="dismissNotificationGroup"
          @mark-all-as-read="onMarkAllNotificationsAsRead"
        />
        <slot />
      </div>
    </Main>

    <aside
      v-if="displayPanel"
      :class="$style.panel"
    >
      <NotificationPanel @notification="notification" />
    </aside>

    <Onboarding v-if="countryParam.ONBOARDING_GUIDES" />
  </div>
</template>

<script lang="ts" setup>
import { Main, Space200 } from '~/components-daisy/layouting';
import { ref, onUpdated, onMounted, computed } from 'vue';
import BumperCar from '~/assets/sounds/bumper_car.mp3';
import NewOrderSound from '~/assets/sounds/new-order.mp3';
import type { CountryParamsType } from '~/app/types/countryParamsType';
import { NotificationType, type PFSNotification } from '~/app/types/notificationType';
import {
  markNotificationAsReadByFloristIdAndOrderId,
  markAllNotificationsOfTypeAsRead,
} from '~/composables/notifications/useNotifications';
import { useActiveNotificationsState } from '~/composables/notifications/useActiveNotificationsState';

const store = useFloristInfoStore();
const { floristInfo: floristInfo } = storeToRefs(store);

const {
  orders: order,
  modifications: modification,
  cancellations: cancellation,
  expired,
  refreshActiveNotifications,
} = useActiveNotificationsState();
const review = ref([]);
const transmission = ref([]);
await refreshActiveNotifications(floristInfo.value.floristId, false);

const config = useRuntimeConfig();
const displayPanel = ref(false);
const newNotifications = ref(true);
const nbNotif = ref(0);
const intervalNotifications = config.public.intervalNotifications;
const isInternalUser = ref(store.isInternalUser);
const localStorageNotEmpty = computed(() => {
  return localStorage.length > 0;
});
const countryParam: CountryParamsType = useCountryParams();
const zendeskURL: string = countryParam.ZENDESK_URL;

onMounted(() => {
  const dataLayer: any = () => {
    (window as any).dataLayer = (window as any).dataLayer || [];
  };

  const gtm = (w: any, d: Document, s: string, l: any, i: string) => {
    w[l] = w[l] || [];
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
    const f: any = d.getElementsByTagName(s)[0],
      j: any = d.createElement(s),
      dl: any = l != 'dataLayer' ? '&l=' + l : '';
    j.async = true;
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    f.parentNode.insertBefore(j, f);
  };

  useHead({
    script: [
      dataLayer(),
      gtm(window, document, 'script', 'dataLayer', 'GTM-5C4W6N86'),
      { id: 'ze-snippet', src: zendeskURL },
    ],
  });

  newNotif();
  playNewOrderSound();

  setInterval(function () {
    newNotif();
    processedNotificationGroups.value = [];
    playNewOrderSound();
  }, intervalNotifications);
});

const notification = (value: boolean) => {
  displayPanel.value = value;
  if (!displayPanel.value) {
    markAsRead();
  }
};

onUpdated(() => {
  refreshActiveNotifications(floristInfo.value.floristId, false);
  processedNotificationGroups.value = [];
  newNotif();
});

const newNotif = async () => {
  await refreshActiveNotifications(floristInfo.value.floristId, false);
  nbNotif.value = 0;

  order.value?.forEach((element: any) => {
    if (!element.isRead) {
      nbNotif.value++;
    }
  });

  modification.value?.forEach((element: any) => {
    if (!element.isRead) {
      nbNotif.value++;
    }
  });

  cancellation.value?.forEach((element: any) => {
    if (!element.isRead) {
      nbNotif.value++;
    }
  });

  expired.value?.forEach((element: any) => {
    if (!element.isRead) {
      nbNotif.value++;
    }
  });

  newNotifications.value = nbNotif.value > 0;
};

const playNewOrderSound = () => {
  if (nbNotif.value > 0) {
    if (isSpanishFlorist) {
      //new Audio(BumperCar).play();
      AddAndPlayAudio(BumperCar);
    } else {
      //new Audio(NewOrderSound).play();
      AddAndPlayAudio(NewOrderSound);
    }
  }
};

const AddAndPlayAudio = (source: string) => {
  var src = document.createElement('source');
  src.setAttribute('src', source);

  const existingAudio = document.body.querySelector('audio');
  if (existingAudio) {
    document.body.removeChild(existingAudio);
  }
  const newAudio = document.createElement('audio');
  newAudio.setAttribute('id', 'audio');
  newAudio.setAttribute('hidden', 'true');
  newAudio.setAttribute('autoplay', '');
  newAudio.setAttribute('controls', '');
  newAudio.appendChild(src);
  document.body.appendChild(newAudio);
  newAudio.play();
};

const markAsRead = () => {
  nbNotif.value = 0;
};

const processedNotificationGroups = ref<NotificationType[]>([]);

const dismissNotificationGroup = (type: NotificationType) => {
  processedNotificationGroups.value.push(type);
};

const unprocessedOrders = computed(() => {
  return !processedNotificationGroups.value.includes(NotificationType.ORDER);
});

const unprocessedModifications = computed(() => {
  return !processedNotificationGroups.value.includes(NotificationType.MODIFICATION);
});

const unprocessedReviews = computed(() => {
  return !processedNotificationGroups.value.includes(NotificationType.REVIEW);
});

const unprocessedTransmissions = computed(() => {
  return !processedNotificationGroups.value.includes(NotificationType.TRANSMISSION);
});

const unprocessedCancellation = computed(() => {
  return !processedNotificationGroups.value.includes(NotificationType.CANCELLATION);
});

const unprocessedExpired = computed(() => {
  return !processedNotificationGroups.value.includes(NotificationType.EXPIRED);
});

const filteredNotifications = computed(() => {
  return [
    unprocessedOrders.value ? order.value : [],
    unprocessedModifications.value ? modification.value : [],
    unprocessedReviews.value ? review.value : [],
    unprocessedTransmissions.value ? transmission.value : [],
    unprocessedCancellation.value ? cancellation.value : [],
    unprocessedExpired.value ? expired.value : [],
  ];
});

const onProcessedCancelOrder = async (orderId: string) => {
  const request = {
    ctId: orderId,
    floristId: floristInfo.value.floristId,
  };
  markNotificationAsReadByFloristIdAndOrderId(request).then(async () => {
    await refreshActiveNotifications(floristInfo.value.floristId, false);
  });
};

const onMarkAllNotificationsAsRead = async (notificationTable: PFSNotification[]) => {
  await markAllNotificationsOfTypeAsRead(floristInfo.value.floristId, notificationTable);
  await refreshActiveNotifications(floristInfo.value.floristId, false);
};
</script>

<style lang="postcss" module>
@import url(assets/css/iris-color.css);
@import url(assets/css/borders.css);
@import url(assets/css/colors.css);
@import url(assets/css/easing.css);
@import url(assets/css/shadows.css);
@import url(assets/css/spacing.css);
@import url(assets/css/typography.css);

/* Force dispalying scrollbar */

* {
  scrollbar-width: 5px;
  scrollbar-color: var(--color-primary-700) transparent;
}

.shell {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: stretch;
  @media (max-width: 900px) {
    flex-direction: column;
  }
}

.main {
  padding: 0 var(--space-800);
  padding-bottom: var(--space-800);
  width: 93%;
  @media (max-width: 900px) {
    padding: 0 var(--space-400);
    width: 100%;
  }
}

.tabs {
  width: 82px;
  min-width: 82px;
  @media (max-width: 900px) {
    width: 100%;
  }
}

.panel {
  background-color: var(--color-mono-100);
  border-left: 1px solid var(--color-mono-300);
  box-shadow: var(--shadow-200);
  width: 30%;
  min-width: 375px;
  height: 100%;
  margin-right: 5px;
  top: 0;
  right: 0;
  position: fixed;
  z-index: 50;
  @media (max-width: 500px) {
    width: 100%;
    min-width: fit-content;
    margin: 0px;
  }
}

.button {
  &--container {
    width: fit-content;
    margin-bottom: 10px;
  }
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 20px;
}

.loginAs {
  font-family: var(--font-400-family);
  text-align: center;
}
</style>
